import * as React from 'react'

/**
 * The player's colors, taken from the cover art the way Spotify does it: the
 * cover's dominant color fills the hero and the embed card, and an accent
 * derived from it paints the controls. Everything is checked for contrast
 * (WCAG AA) against what it sits on, in the light and in the dark theme. With
 * no cover, or a cover the browser cannot read (no CORS), the community's
 * primary color stands in.
 */

export type Rgb = [number, number, number]

export interface PlayerPalette {
  /** Fill of the hero and the embed card: white text reads on it (≥ 4.5:1). */
  hero: Rgb
  /** The hero's darker end, for the gradient. */
  heroDeep: Rgb
  /** Filled controls (play button, progress) on the theme surface (≥ 3:1). */
  accent: Rgb
  /** Icon/text on `accent`. */
  onAccent: Rgb
  /** Active icons and highlighted text on the theme surface (≥ 4.5:1). */
  accentText: Rgb
}

const WHITE: Rgb = [255, 255, 255]
const BLACK: Rgb = [0, 0, 0]

const channel = (value: number) => {
  const c = value / 255
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
}
export const luminance = ([r, g, b]: Rgb) => 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
export const contrast = (a: Rgb, b: Rgb) => {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x)
  return (hi + 0.05) / (lo + 0.05)
}
const mix = (a: Rgb, b: Rgb, t: number): Rgb => [0, 1, 2].map((i) => Math.round(a[i] + (b[i] - a[i]) * t)) as Rgb

/** Moves `color` toward `toward` until it reaches `target` contrast with `against`. */
const pushUntil = (color: Rgb, toward: Rgb, against: Rgb, target: number): Rgb => {
  for (let t = 0; t <= 1.0001; t += 0.04) {
    const candidate = mix(color, toward, t)
    if (contrast(candidate, against) >= target) return candidate
  }
  return toward
}

/** HSL saturation, 0–1. */
const saturationOf = ([r, g, b]: Rgb) => {
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  return max === min ? 0 : (max - min) / (255 - Math.abs(max + min - 255))
}

/** "12 34 56" for `rgb(var(--x))`. */
export const rgbChannels = (color: Rgb) => color.join(' ')

const parseColor = (raw: string): Rgb | null => {
  const value = raw.trim()
  const hex = value.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i)
  if (hex) {
    const h = hex[1].length === 3 ? hex[1].split('').map((c) => c + c).join('') : hex[1]
    return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)) as Rgb
  }
  const parts = value.replace(/rgba?\(|\)/g, '').split(/[\s,/]+/).filter(Boolean).slice(0, 3).map(Number)
  return parts.length === 3 && parts.every((n) => Number.isFinite(n)) ? (parts as Rgb) : null
}

const cssVar = (name: string): Rgb | null => {
  if (typeof document === 'undefined') return null
  return parseColor(getComputedStyle(document.documentElement).getPropertyValue(name))
}

/** The dominant color of an image: the most common saturated bucket, averaged. */
const dominantOf = (image: CanvasImageSource): Rgb | null => {
  const size = 32
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const context = canvas.getContext('2d', { willReadFrequently: true })
  if (!context) return null
  context.drawImage(image, 0, 0, size, size)
  const { data } = context.getImageData(0, 0, size, size)
  const buckets = new Map<number, { weight: number; sum: [number, number, number]; count: number }>()
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] < 128) continue
    const [r, g, b] = [data[i], data[i + 1], data[i + 2]]
    const max = Math.max(r, g, b)
    const min = Math.min(r, g, b)
    const light = (max + min) / 510
    const saturation = max === min ? 0 : (max - min) / (255 - Math.abs(max + min - 255))
    /* Near-white and near-black say little about the art; saturated pixels say the most. */
    const weight = (light < 0.08 || light > 0.94 ? 0.1 : 1) * (0.2 + saturation * saturation * 6)
    const key = ((r >> 4) << 8) | ((g >> 4) << 4) | (b >> 4)
    const bucket = buckets.get(key) || { weight: 0, sum: [0, 0, 0], count: 0 }
    bucket.weight += weight
    bucket.sum[0] += r
    bucket.sum[1] += g
    bucket.sum[2] += b
    bucket.count += 1
    buckets.set(key, bucket)
  }
  let best: { weight: number; sum: [number, number, number]; count: number } | null = null
  for (const bucket of buckets.values()) if (!best || bucket.weight > best.weight) best = bucket
  return best ? (best.sum.map((v) => Math.round(v / best!.count)) as Rgb) : null
}

/* Signed links change every hour; the file behind them does not. */
const cacheKey = (url: string) => url.split('?')[0]
const dominantCache = new Map<string, Rgb | null>()

/*
 * The cover is fetched again with CORS and without the HTTP cache: the page's
 * own <img> loaded it without CORS, and S3 answers that without `Vary:
 * Origin`, so a CORS image would reuse the cached answer and fail.
 */
const readImage = async (url: string): Promise<CanvasImageSource> => {
  const response = await fetch(url, { mode: 'cors', cache: 'no-store', credentials: 'omit' })
  if (!response.ok) throw new Error(`cover ${response.status}`)
  const blob = await response.blob()
  if (typeof createImageBitmap === 'function') return createImageBitmap(blob)
  const objectUrl = URL.createObjectURL(blob)
  try {
    const image = new Image()
    image.src = objectUrl
    await image.decode()
    return image
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

const pending = new Map<string, Promise<Rgb | null>>()

const loadDominant = (url: string): Promise<Rgb | null> => {
  const key = cacheKey(url)
  if (dominantCache.has(key)) return Promise.resolve(dominantCache.get(key) ?? null)
  const running = pending.get(key)
  if (running) return running
  const job = readImage(url)
    .then((image) => dominantOf(image))
    .catch(() => null /* no CORS on that host, or not an image */)
    .then((color) => { dominantCache.set(key, color); pending.delete(key); return color })
  pending.set(key, job)
  return job
}

/** The palette for a base color on the current theme surface. */
export const paletteFrom = (base: Rgb, surface: Rgb): PlayerPalette => {
  const darkSurface = luminance(surface) < 0.2
  const hero = pushUntil(base, BLACK, WHITE, 5.2)
  const heroDeep = mix(hero, BLACK, 0.45)
  /* The cover's color paints the controls only when it is vivid and reads on the surface as it is; otherwise black or white, as Spotify does. */
  const plain: Rgb = darkSurface ? WHITE : [18, 18, 22]
  const vivid = saturationOf(base) >= 0.35
  const accent = vivid && contrast(base, surface) >= 3 ? base : plain
  const onAccent = contrast(accent, WHITE) >= contrast(accent, BLACK) ? WHITE : [18, 18, 22] as Rgb
  const accentText = vivid && contrast(base, surface) >= 4.5 ? base : plain
  return { hero, heroDeep, accent, onAccent, accentText }
}

/** True while `<html>` carries the dark theme; follows the switch live. */
const useDarkTheme = () => {
  const read = () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
  const [dark, setDark] = React.useState(read)
  React.useEffect(() => {
    if (typeof MutationObserver === 'undefined') return
    const observer = new MutationObserver(() => setDark(read()))
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'style'] })
    return () => observer.disconnect()
  }, [])
  return dark
}

/** The cover's palette (the community color until the cover is read, or when it cannot be). */
export function useCoverPalette(coverUrl: string | null | undefined): PlayerPalette {
  const dark = useDarkTheme()
  const [dominant, setDominant] = React.useState<Rgb | null>(() => (coverUrl ? dominantCache.get(cacheKey(coverUrl)) ?? null : null))
  React.useEffect(() => {
    if (!coverUrl) { setDominant(null); return }
    let canceled = false
    void loadDominant(coverUrl).then((color) => { if (!canceled) setDominant(color) })
    return () => { canceled = true }
  }, [coverUrl])
  return React.useMemo(() => {
    const surface = cssVar('--rgb-surface') || (dark ? [23, 23, 28] as Rgb : WHITE)
    const base = dominant || cssVar('--rgb-primary-600') || [219, 39, 119]
    return paletteFrom(base, surface)
  }, [dominant, dark])
}

/** The palette as CSS custom properties for a player root (`rgb(var(--pl-accent))`). */
export const paletteStyle = (palette: PlayerPalette): React.CSSProperties => ({
  ['--pl-hero' as string]: rgbChannels(palette.hero),
  ['--pl-hero-deep' as string]: rgbChannels(palette.heroDeep),
  ['--pl-accent' as string]: rgbChannels(palette.accent),
  ['--pl-on-accent' as string]: rgbChannels(palette.onAccent),
  ['--pl-accent-text' as string]: rgbChannels(palette.accentText)
})
